import { normalizedSnapTargets, type PageDimensionsMm, type ResizeHandle } from './geometry';
import type { NormalizedPoint, NormalizedRect } from './model';

export interface CompositionSnapTarget {
  readonly id: string;
  readonly bounds: NormalizedRect;
  readonly visible?: boolean;
}

export interface CompositionSnapOptions {
  readonly objectId: string;
  readonly objects: readonly CompositionSnapTarget[];
  readonly grid: boolean;
  readonly objectSnap: boolean;
  readonly page: PageDimensionsMm;
  readonly gridSpacingMm: number;
  /** CSS-pixel threshold converted independently for the actual displayed page axes. */
  readonly tolerance: NormalizedPoint;
  readonly handle?: ResizeHandle | undefined;
  readonly pointGrid?: boolean;
  /** Existing point furniture may extend beyond the page while its anchor remains valid. */
  readonly allowPointOverflow?: boolean;
  readonly aspectRatio?: number | undefined;
  readonly minimumSize?: number;
}

export interface CompositionSnapResult {
  readonly bounds: NormalizedRect;
  readonly guides: { readonly x?: number; readonly y?: number };
}

const validBounds = (bounds: NormalizedRect) =>
  Object.values(bounds).every(Number.isFinite) &&
  bounds.width > 0 &&
  bounds.height > 0 &&
  bounds.x >= 0 &&
  bounds.y >= 0 &&
  bounds.x + bounds.width <= 1 + Number.EPSILON &&
  bounds.y + bounds.height <= 1 + Number.EPSILON;

/** One nearest-candidate resolution per axis, shared by moving and active-edge resizing. */
export function snapCompositionRect(
  bounds: NormalizedRect,
  options: CompositionSnapOptions,
): CompositionSnapResult {
  const guides: { x?: number; y?: number } = {};
  if (
    !validBounds(bounds) &&
    !(
      options.allowPointOverflow &&
      Object.values(bounds).every(Number.isFinite) &&
      bounds.width > 0 &&
      bounds.height > 0
    )
  )
    return { bounds, guides };
  if (options.aspectRatio && options.handle?.includes('-')) {
    const ratio = options.aspectRatio;
    const handle = options.handle;
    const candidates = (['x', 'y'] as const).flatMap((axis) => {
      const side =
        axis === 'x'
          ? handle.includes('west')
            ? 'west'
            : 'east'
          : handle.includes('north')
            ? 'north'
            : 'south';
      const snapped = snapCompositionRect(bounds, {
        ...options,
        handle: side,
        aspectRatio: undefined,
      });
      const width = axis === 'x' ? snapped.bounds.width : snapped.bounds.height * ratio;
      const height = width / ratio;
      const candidate = {
        x: handle.includes('west') ? bounds.x + bounds.width - width : bounds.x,
        y: handle.includes('north') ? bounds.y + bounds.height - height : bounds.y,
        width,
        height,
      };
      const distance = Math.hypot(
        (width - bounds.width) / options.tolerance.x,
        (height - bounds.height) / options.tolerance.y,
      );
      if (
        distance === 0 ||
        distance > 1 ||
        !validBounds(candidate) ||
        width < (options.minimumSize ?? 0.05) ||
        height < (options.minimumSize ?? 0.05)
      )
        return [];
      return [{ bounds: candidate, distance }];
    });
    const nearest = candidates.sort((a, b) => a.distance - b.distance)[0];
    if (!nearest) return { bounds, guides };
    const aligned = snapCompositionRect(nearest.bounds, {
      ...options,
      aspectRatio: undefined,
      tolerance: { x: 1e-12, y: 1e-12 },
    });
    return { bounds: nearest.bounds, guides: aligned.guides };
  }
  const objects = options.objectSnap
    ? options.objects.filter(
        ({ id, visible, bounds: box }) =>
          id !== options.objectId && visible !== false && validBounds(box),
      )
    : [];
  const result = { ...bounds };
  for (const axis of ['x', 'y'] as const) {
    const dimension = axis === 'x' ? 'width' : 'height';
    const lowHandle = axis === 'x' ? 'west' : 'north';
    const highHandle = axis === 'x' ? 'east' : 'south';
    const lowActive = options.handle?.includes(lowHandle);
    const highActive = options.handle?.includes(highHandle);
    if (options.handle && !lowActive && !highActive) continue;
    const start = bounds[axis];
    const size = bounds[dimension];
    const end = start + size;
    const moving = options.handle ? [lowActive ? start : end] : [start, start + size / 2, end];
    const targets = [
      ...objects
        .flatMap(({ bounds: box }) => [
          box[axis],
          box[axis] + box[dimension] / 2,
          box[axis] + box[dimension],
        ])
        .map((value) => ({ value, object: true })),
      ...(options.grid
        ? normalizedSnapTargets(
            axis === 'x' ? options.page.width : options.page.height,
            options.gridSpacingMm,
          ).map((value) => ({ value, object: false }))
        : []),
    ];
    let nearest: { correction: number; value: number; object: boolean } | undefined;
    for (const edge of moving) {
      for (const target of targets) {
        if (options.pointGrid && !options.handle && !target.object && edge !== start) continue;
        const correction = target.value - edge;
        if (Math.abs(correction) > options.tolerance[axis]) continue;
        const nextStart = !options.handle || lowActive ? start + correction : start;
        const nextEnd = !options.handle || highActive ? end + correction : end;
        const nextSize = nextEnd - nextStart;
        if (
          nextStart < 0 ||
          (options.allowPointOverflow && !options.handle ? nextStart > 0.98 : nextEnd > 1) ||
          (options.handle && nextSize < (options.minimumSize ?? 0.05))
        )
          continue;
        if (!nearest || Math.abs(correction) < Math.abs(nearest.correction))
          nearest = { correction, ...target };
      }
    }
    if (!nearest) continue;
    if (!options.handle) result[axis] += nearest.correction;
    else if (lowActive) {
      result[axis] += nearest.correction;
      result[dimension] -= nearest.correction;
    } else result[dimension] += nearest.correction;
    if (nearest.object) guides[axis] = nearest.value;
  }
  return { bounds: result, guides };
}
